import React from "react";
import { useMutation } from "@tanstack/react-query";
import { updateScheduleItem } from "api";
import { Modal } from "components/common";
import type { ScheduleItem, ScheduleItemStatus } from "types";

const ALL_STATUSES: { value: ScheduleItemStatus; label: string }[] = [
  { value: "created",            label: "Created" },
  { value: "pending_assignment", label: "Pending assignment" },
  { value: "scheduled",          label: "Scheduled" },
  { value: "in_progress",        label: "In progress" },
  { value: "completed",          label: "Completed" },
  { value: "abandoned",          label: "Abandoned" },
  { value: "cancelled",          label: "Cancelled" },
  { value: "blacked_out",        label: "Blacked out" },
];

export function ChangeStatusModal({
  item,
  partnerName,
  allowedStatuses,
  onClose,
  onSuccess,
}: {
  item:             ScheduleItem;
  partnerName:      string;
  /** When provided, only these statuses are shown. Omit to show all. */
  allowedStatuses?: ScheduleItemStatus[];
  onClose:          () => void;
  onSuccess:        () => void;
}) {
  const { mutate, isPending } = useMutation({
    mutationFn: (status: ScheduleItemStatus) => updateScheduleItem(item.id, { status }),
    onSuccess,
  });

  const visibleStatuses = allowedStatuses
    ? ALL_STATUSES.filter(({ value }) => allowedStatuses.includes(value))
    : ALL_STATUSES;

  return (
    <Modal title={`Change status — ${partnerName}`} onClose={onClose}>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {visibleStatuses.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => mutate(value)}
            disabled={isPending}
            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors hover:bg-gray-50 flex items-center justify-between ${
              item.status === value ? "bg-brand-50 text-brand-700" : "text-gray-700"
            }`}
          >
            <span>{label}</span>
            {item.status === value && (
              <span className="text-xs text-brand-500">Current</span>
            )}
          </button>
        ))}
      </div>
    </Modal>
  );
}
